import React, { FC, PropsWithChildren, ReactNode, useEffect } from "react";
import { useLegoland } from "../LegolandContext";
import { useMeQuery } from "./authApiHooks";
import { UserRole } from "./authApiTypes";

/** Properties for the RequireAuth hook. */
export type RequireAuthHookProps = {
  /**
   * If true, the user will be redirected to the login page if they are not
   * authenticated.
   */
  autoRedirect?: boolean;
  /**
   * The role(s) required to access the component. If left undefined, the user
   * only needs to be authenticated.
   */
  role?: UserRole | UserRole[];
};

export type RequireAuthProps = RequireAuthHookProps &
  PropsWithChildren<{
    /**
     * The fallback content to render if the user does not have the required
     * role(s) or is not authenticated (if `autoRedirect` is false).
     */
    fallback?: ReactNode;
  }>;

/** The return type of the RequireAuth hook. */
export type RequireAuthHookReturnType = {
  /** The error returned by the query. */
  error: unknown;
  /**
   * Whether the user has the required role(s) or is authenticated when no role
   * is required. If undefined, the user's authentication state is still being
   * determined or the user is not authenticated.
   */
  hasValidRole: boolean | undefined;
  /**
   * Whether the user is authenticated. If undefined, the user's authentication
   * state is still being determined.
   */
  isAuthenticated: boolean | undefined;
  /**
   * Whether the query is in an error state. If true, the `error` property will
   * contain the error.
   */
  isError: boolean;
  /** Whether the query is loading. */
  isLoading: boolean;
  /** Whether the query finished and was successful. */
  isSuccess: boolean;
};

/**
 * A hook that checks if the user is authenticated and has the required role(s).
 * If the user is not authenticated and `autoRedirect` is true, the user will be
 * redirected to the login page.
 */
export function useRequireAuth({
  autoRedirect,
  role,
}: RequireAuthHookProps): RequireAuthHookReturnType {
  if (typeof role === "string") {
    role = [role];
  }

  const { legolandUiUrl } = useLegoland();
  const { data, error, isError, isLoading, isSuccess } = useMeQuery();

  useEffect(
    function onAuthStateChangeEffect() {
      if (autoRedirect && isSuccess && data!.me === null) {
        window.location.assign(
          `${legolandUiUrl}/login?redirect=${encodeURIComponent(
            window.location.toString(),
          )}`,
        );
      }
    },
    [autoRedirect, isSuccess, data],
  );

  const queryStatus = { error, isError, isLoading, isSuccess };

  if (!isSuccess) {
    return {
      ...queryStatus,
      hasValidRole: undefined,
      isAuthenticated: undefined,
    };
  }

  const isAuthenticated = Boolean(data!.me);

  if (!isAuthenticated) {
    return {
      ...queryStatus,
      hasValidRole: undefined,
      isAuthenticated,
    };
  }

  const hasValidRole = !role || role.includes(data!.me!.role);

  return {
    ...queryStatus,
    hasValidRole,
    isAuthenticated,
  };
}

/**
 * A component that checks if the user is authenticated and has the required
 * role(s). If the user is not authenticated and `autoRedirect` is true, the
 * user will be redirected to the login page. If the user does not have the
 * required role(s) or is not authenticated (if `autoRedirect` is false and no
 * role is provided), the `fallback` content will be rendered.
 */
export const RequireAuth: FC<RequireAuthProps> = ({
  autoRedirect = false,
  children,
  fallback = null,
  role,
}) => {
  const { hasValidRole, isAuthenticated } = useRequireAuth({
    autoRedirect,
    role,
  });

  if (isAuthenticated === undefined) {
    return null;
  }

  if (!isAuthenticated || !hasValidRole) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
